import React, { useEffect, useState } from 'react';
import {
  collection, getDocs, doc, getDoc, setDoc, updateDoc, query, where
} from 'firebase/firestore';
import { db } from '../firebase';

const DEFAULT_BUCKETS = [
  { id: 'business_model',    label: 'Business Model',       icon: '🗂',  description: 'Optimising or pivoting the business model' },
  { id: 'technology',        label: 'Technology & Systems',  icon: '⚙️', description: 'Developing or upgrading technology' },
  { id: 'customer_journey',  label: 'Customer Journey',      icon: '🗺',  description: 'Improving user experience and customer touchpoints' },
  { id: 'learning_outcomes', label: 'Learning Outcomes',     icon: '📈',  description: 'Validating and improving educational impact' },
];

const STAGE_COLORS = {
  'Problem-Solution Fit':        '#1d4ed8',
  'Product-Market Fit':          '#0369a1',
  'Early Growth & Monetization': '#166534',
  'Scaling & Regional Expansion':'#065f46',
};

export default function ProgrammeManager() {
  const [clients, setClients]     = useState([]);
  const [programs, setPrograms]   = useState([]);
  const [cohorts, setCohorts]     = useState([]);
  const [companies, setCompanies] = useState([]);
  const [rubrics, setRubrics]     = useState([]);
  const [loading, setLoading]     = useState(true);

  const [view, setView]           = useState('list'); // list | newClient | newProgram | newCohort | cohortDetail
  const [filterClient, setFilterClient] = useState('all');
  const [filterProgram, setFilterProgram] = useState('all');
  const [renamingId, setRenamingId] = useState(null);
  const [renameDraft, setRenameDraft] = useState('');
  const [selectedClient, setSelectedClient]   = useState(null);
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [selectedCohort, setSelectedCohort]   = useState(null);
  const [saving, setSaving]       = useState(false);
  const [saved, setSaved]         = useState(false);
  const [error, setError]         = useState(null);

  // Forms
  const [clientForm, setClientForm] = useState({ name: '', domain: '' });
  const [programForm, setProgramForm] = useState({ name: '', startDate: '', endDate: '' });
  const [cohortForm, setCohortForm] = useState({
    name: '', currentPeriod: '',
    enableMetric: false,
    metricLabel: 'Active learners',
    metricTarget: 8000,
    buckets: DEFAULT_BUCKETS,
    rubricTemplateId: 'tmpl_prog_rfc_ethiopia_2026',
  });
  const [newBucket, setNewBucket] = useState({ id: '', label: '', icon: '📋', description: '' });

  useEffect(() => { load(); }, []);

  async function load() {
    try {
      const [cSnap, pSnap, coSnap, compSnap, rSnap] = await Promise.all([
        getDocs(collection(db, 'clients')),
        getDocs(collection(db, 'programs')),
        getDocs(collection(db, 'cohorts')),
        getDocs(collection(db, 'companies')),
        getDocs(query(collection(db, 'rubricTemplates'), where('isVariant', '==', true))),
      ]);
      setClients(cSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      setPrograms(pSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      setCohorts(coSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      setCompanies(compSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      // Base template + variants
      const baseSnap = await getDoc(doc(db, 'rubricTemplates', 'tmpl_prog_rfc_ethiopia_2026'));
      const allRubrics = [{ id: baseSnap.id, name: 'Base (generic)', ...baseSnap.data() }];
      rSnap.docs.forEach(d => allRubrics.push({ id: d.id, ...d.data() }));
      setRubrics(allRubrics);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }

  // ── Save client ──────────────────────────────────────────────
  const saveClient = async () => {
    if (!clientForm.name.trim()) return;
    setSaving(true); setError(null);
    try {
      const id = clientForm.name.toLowerCase().replace(/[^a-z0-9]/g, '').slice(0, 24) + '_' + Date.now().toString(36);
      await setDoc(doc(db, 'clients', id), {
        id, name: clientForm.name, domain: clientForm.domain,
        programIds: [], createdAt: new Date(),
      });
      await load();
      setView('list');
      setClientForm({ name: '', domain: '' });
      setSaved(true); setTimeout(() => setSaved(false), 2000);
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  };

  // ── Save program ─────────────────────────────────────────────
  const saveProgram = async () => {
    if (!programForm.name.trim() || !selectedClient) return;
    setSaving(true); setError(null);
    try {
      const id = 'prog_' + programForm.name.toLowerCase().replace(/[^a-z0-9]/g, '_').slice(0, 24) + '_' + Date.now().toString(36);
      await setDoc(doc(db, 'programs', id), {
        id, name: programForm.name, clientId: selectedClient.id,
        clientName: selectedClient.name,
        currentPeriod: 'Q1-' + new Date().getFullYear(),
        rubricTemplateId: 'tmpl_prog_rfc_ethiopia_2026',
        startDate: programForm.startDate ? new Date(programForm.startDate) : new Date(),
        endDate: programForm.endDate ? new Date(programForm.endDate) : null,
        createdAt: new Date(),
      });
      // Add program to client
      await updateDoc(doc(db, 'clients', selectedClient.id), {
        programIds: [...(selectedClient.programIds || []), id],
      });
      await load();
      setView('list');
      setProgramForm({ name: '', startDate: '', endDate: '' });
      setSaved(true); setTimeout(() => setSaved(false), 2000);
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  };

  // ── Save cohort ──────────────────────────────────────────────
  const saveCohort = async () => {
    if (!cohortForm.name.trim() || !selectedProgram) return;
    setSaving(true); setError(null);
    try {
      const id = 'cohort_' + cohortForm.name.toLowerCase().replace(/[^a-z0-9]/g, '_').slice(0, 30) + '_' + Date.now().toString(36);
      const cohortData = {
        id, name: cohortForm.name,
        programId: selectedProgram.id,
        currentPeriod: cohortForm.currentPeriod || 'Q1-' + new Date().getFullYear(),
        rubricTemplateId: cohortForm.rubricTemplateId,
        buckets: cohortForm.buckets,
        createdAt: new Date(),
      };
      if (cohortForm.enableMetric && cohortForm.metricLabel.trim()) {
        cohortData.targets = {
          primaryMetric: {
            label: cohortForm.metricLabel.trim(),
            avgPerCompany: parseInt(cohortForm.metricTarget) || 0,
            workPlanMetricKey: 'Number of active learners this period',
            updatedAt: new Date(),
          }
        };
      }
      await setDoc(doc(db, 'cohorts', id), cohortData);
      await load();
      setView('list');
      setCohortForm({ name: '', currentPeriod: '', enableMetric: false, metricLabel: 'Active learners', metricTarget: 8000, buckets: DEFAULT_BUCKETS, rubricTemplateId: 'tmpl_prog_rfc_ethiopia_2026' });
      setSaved(true); setTimeout(() => setSaved(false), 2000);
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  };

  // ── Assign company to cohort ──────────────────────────────────
  const assignCompanyToCohort = async (companyId, cohortId) => {
    try {
      await updateDoc(doc(db, 'companies', companyId), { cohortId });
      setCompanies(prev => prev.map(c => c.id === companyId ? { ...c, cohortId } : c));
    } catch (err) { console.error(err); }
  };

  // ── Assign rubric to cohort ───────────────────────────────────
  const assignRubricToCohort = async (cohortId, rubricId) => {
    try {
      await updateDoc(doc(db, 'cohorts', cohortId), { rubricTemplateId: rubricId });
      setCohorts(prev => prev.map(c => c.id === cohortId ? { ...c, rubricTemplateId: rubricId } : c));
      setSaved(true); setTimeout(() => setSaved(false), 2000);
    } catch (err) { console.error(err); }
  };

  // ── Rename client (cascades clientName onto its programs) ──────
  const renameClient = async (clientId, newName) => {
    if (!newName.trim()) return;
    try {
      await updateDoc(doc(db, 'clients', clientId), { name: newName.trim() });
      const affectedPrograms = programs.filter(p => p.clientId === clientId);
      await Promise.all(affectedPrograms.map(p =>
        updateDoc(doc(db, 'programs', p.id), { clientName: newName.trim() })
      ));
      setClients(prev => prev.map(c => c.id === clientId ? { ...c, name: newName.trim() } : c));
      setPrograms(prev => prev.map(p => p.clientId === clientId ? { ...p, clientName: newName.trim() } : p));
      setRenamingId(null);
      setSaved(true); setTimeout(() => setSaved(false), 2000);
    } catch (err) { console.error(err); }
  };

  // ── Rename program ───────────────────────────────────────────
  const renameProgram = async (programId, newName) => {
    if (!newName.trim()) return;
    try {
      await updateDoc(doc(db, 'programs', programId), { name: newName.trim() });
      setPrograms(prev => prev.map(p => p.id === programId ? { ...p, name: newName.trim() } : p));
      setRenamingId(null);
      setSaved(true); setTimeout(() => setSaved(false), 2000);
    } catch (err) { console.error(err); }
  };

  if (loading) return <div className="gm-loading"><div className="gm-spinner" /></div>;

  // ── Hierarchy view ────────────────────────────────────────────
  if (view === 'list') return (
    <div className="gm-programme-manager">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h2 className="gm-section-title" style={{ marginBottom: 4 }}>Programme hierarchy</h2>
          <p style={{ fontSize: '0.78rem', color: '#6b6b6b' }}>Client → Program → Cohort → Companies</p>
        </div>
        {saved && <span className="gm-save-success">✓ Saved</span>}
        <button className="gm-btn gm-btn-primary" onClick={() => { setSelectedClient(null); setView('newClient'); }}>
          + New client
        </button>
      </div>

      {/* Client/Program filter */}
      {clients.length > 1 && (
        <div style={{ display:'flex', gap:12, marginBottom:16, flexWrap:'wrap', alignItems:'center' }}>
          <select value={filterClient} onChange={e => { setFilterClient(e.target.value); setFilterProgram('all'); }}
            style={{ padding:'8px 12px', border:'1.5px solid #e2e8f0', borderRadius:6, fontSize:'0.82rem', fontFamily:'inherit', minWidth:200 }}>
            <option value="all">All clients</option>
            {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          {filterClient !== 'all' && (
            <select value={filterProgram} onChange={e => setFilterProgram(e.target.value)}
              style={{ padding:'8px 12px', border:'1.5px solid #e2e8f0', borderRadius:6, fontSize:'0.82rem', fontFamily:'inherit', minWidth:200 }}>
              <option value="all">All programs</option>
              {programs.filter(p => p.clientId === filterClient).map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          )}
          {(filterClient !== 'all' || filterProgram !== 'all') && (
            <button className="gm-btn gm-btn-sm gm-btn-outline" onClick={() => { setFilterClient('all'); setFilterProgram('all'); }}>
              Clear filter
            </button>
          )}
        </div>
      )}

      {clients.length === 0 && (
        <div className="gm-empty-state"><p>No clients yet. Add your first client to get started.</p></div>
      )}

      {clients.filter(c => filterClient === 'all' || c.id === filterClient).map(client => {
        const clientPrograms = programs.filter(p => p.clientId === client.id && (filterProgram === 'all' || p.id === filterProgram));
        return (
          <div key={client.id} className="gm-hierarchy-client">
            <div className="gm-hierarchy-client-header">
              <div style={{ flex: 1 }}>
                {renamingId === `client_${client.id}` ? (
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <input type="text" value={renameDraft} onChange={e => setRenameDraft(e.target.value)}
                      autoFocus
                      style={{ padding: '4px 10px', borderRadius: 4, border: 'none', fontSize: '0.95rem', fontWeight: 700, color: '#1e293b' }} />
                    <button className="gm-btn gm-btn-sm" style={{ background: 'white', color: '#2E3A6B' }}
                      onClick={() => renameClient(client.id, renameDraft)}>✓</button>
                    <button className="gm-btn gm-btn-sm" style={{ background: 'transparent', color: 'white', border: '1px solid rgba(255,255,255,0.4)' }}
                      onClick={() => setRenamingId(null)}>✕</button>
                  </div>
                ) : (
                  <div className="gm-hierarchy-client-name" style={{ cursor: 'pointer' }}
                    onClick={() => { setRenamingId(`client_${client.id}`); setRenameDraft(client.name); }}
                    title="Click to rename">
                    {client.name} <span style={{ fontSize: '0.7rem', opacity: 0.6 }}>✏️</span>
                  </div>
                )}
                <div className="gm-hierarchy-client-domain">{client.domain}</div>
              </div>
              <button className="gm-btn gm-btn-sm gm-btn-outline"
                onClick={() => { setSelectedClient(client); setView('newProgram'); }}>
                + Program
              </button>
            </div>

            {clientPrograms.map(program => {
              const programCohorts = cohorts.filter(c => c.programId === program.id);
              return (
                <div key={program.id} className="gm-hierarchy-program">
                  <div className="gm-hierarchy-program-header">
                    <div style={{ flex: 1 }}>
                      {renamingId === `program_${program.id}` ? (
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                          <input type="text" value={renameDraft} onChange={e => setRenameDraft(e.target.value)}
                            autoFocus
                            style={{ padding: '4px 10px', borderRadius: 4, border: '1px solid #cbd5e1', fontSize: '0.875rem', fontWeight: 700 }} />
                          <button className="gm-btn gm-btn-sm gm-btn-primary" onClick={() => renameProgram(program.id, renameDraft)}>✓</button>
                          <button className="gm-btn gm-btn-sm gm-btn-outline" onClick={() => setRenamingId(null)}>✕</button>
                        </div>
                      ) : (
                        <div className="gm-hierarchy-program-name" style={{ cursor: 'pointer' }}
                          onClick={() => { setRenamingId(`program_${program.id}`); setRenameDraft(program.name); }}
                          title="Click to rename">
                          {program.name} <span style={{ fontSize: '0.65rem', opacity: 0.5 }}>✏️</span>
                        </div>
                      )}
                      <div className="gm-hierarchy-meta">
                        {program.currentPeriod && <span>Period: {program.currentPeriod}</span>}
                        {program.startDate && <span>Started: {program.startDate?.toDate ? program.startDate.toDate().getFullYear() : new Date(program.startDate).getFullYear()}</span>}
                      </div>
                    </div>
                    <button className="gm-btn gm-btn-sm gm-btn-outline"
                      onClick={() => { setSelectedProgram(program); setSelectedClient(client); setView('newCohort'); }}>
                      + Cohort
                    </button>
                  </div>

                  {programCohorts.map(cohort => {
                    const cohortCompanies = companies.filter(c => c.cohortId === cohort.id);
                    const unassigned = companies.filter(c => !c.cohortId && c.programId === program.id);
                    return (
                      <div key={cohort.id} className="gm-hierarchy-cohort">
                        <div className="gm-hierarchy-cohort-header">
                          <div>
                            <div className="gm-hierarchy-cohort-name">{cohort.name}</div>
                            <div className="gm-hierarchy-meta">
                              <span>{cohortCompanies.length} companies</span>
                              <span>Period: {cohort.currentPeriod || '—'}</span>
                            </div>
                          </div>
                          <button className="gm-btn gm-btn-sm gm-btn-outline"
                            onClick={() => { setSelectedCohort(cohort); setSelectedProgram(program); setView('cohortDetail'); }}>
                            Manage →
                          </button>
                        </div>

                        {/* Companies in cohort */}
                        {cohortCompanies.length > 0 && (
                          <div className="gm-hierarchy-companies">
                            {cohortCompanies.map(c => {
                              const color = STAGE_COLORS[c.currentStage] || '#4a5481';
                              return (
                                <div key={c.id} className="gm-hierarchy-company">
                                  <span className="gm-hierarchy-company-name">{c.name}</span>
                                  <span style={{ fontSize: '0.72rem', color, fontWeight: 600 }}>{c.currentStage}</span>
                                </div>
                              );
                            })}
                          </div>
                        )}

                        {/* Unassigned companies */}
                        {unassigned.length > 0 && (
                          <div style={{ padding: '8px 12px', borderTop: '1px dashed #e2e8f0' }}>
                            <div style={{ fontSize: '0.72rem', color: '#94a3b8', marginBottom: 6 }}>Unassigned companies — click to add to this cohort:</div>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                              {unassigned.map(c => (
                                <button key={c.id}
                                  className="gm-btn gm-btn-sm gm-btn-outline"
                                  style={{ fontSize: '0.72rem' }}
                                  onClick={() => assignCompanyToCohort(c.id, cohort.id)}>
                                  + {c.name}
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}

                  {programCohorts.length === 0 && (
                    <div style={{ padding: '12px 24px', color: '#94a3b8', fontSize: '0.78rem', fontStyle: 'italic' }}>
                      No cohorts yet — add one above
                    </div>
                  )}
                </div>
              );
            })}

            {clientPrograms.length === 0 && (
              <div style={{ padding: '12px 20px', color: '#94a3b8', fontSize: '0.78rem', fontStyle: 'italic' }}>
                No programs yet — add one above
              </div>
            )}
          </div>
        );
      })}
    </div>
  );

  // ── New client form ───────────────────────────────────────────
  if (view === 'newClient') return (
    <FormShell title="New client" onBack={() => setView('list')} onSave={saveClient} saving={saving} error={error}>
      <div className="gm-form-group">
        <label>Organisation name *</label>
        <input type="text" value={clientForm.name} onChange={e => setClientForm(p => ({ ...p, name: e.target.value }))}
          placeholder="e.g. Reach for Change Ethiopia" />
      </div>
      <div className="gm-form-group">
        <label>Domain / website</label>
        <input type="text" value={clientForm.domain} onChange={e => setClientForm(p => ({ ...p, domain: e.target.value }))}
          placeholder="e.g. reachforchange.org" />
      </div>
    </FormShell>
  );

  // ── New program form ──────────────────────────────────────────
  if (view === 'newProgram') return (
    <FormShell title={`New program — ${selectedClient?.name}`} onBack={() => setView('list')} onSave={saveProgram} saving={saving} error={error}>
      <div className="gm-form-group">
        <label>Programme name *</label>
        <input type="text" value={programForm.name} onChange={e => setProgramForm(p => ({ ...p, name: e.target.value }))}
          placeholder="e.g. Scaling Readiness Programme — West Africa Fintech 2026" />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div className="gm-form-group">
          <label>Start date</label>
          <input type="date" value={programForm.startDate} onChange={e => setProgramForm(p => ({ ...p, startDate: e.target.value }))} />
        </div>
        <div className="gm-form-group">
          <label>End date</label>
          <input type="date" value={programForm.endDate} onChange={e => setProgramForm(p => ({ ...p, endDate: e.target.value }))} />
        </div>
      </div>
    </FormShell>
  );

  // ── New cohort form ───────────────────────────────────────────
  if (view === 'newCohort') return (
    <FormShell title={`New cohort — ${selectedProgram?.name}`} onBack={() => setView('list')} onSave={saveCohort} saving={saving} error={error}>
      <div className="gm-form-group">
        <label>Cohort name *</label>
        <input type="text" value={cohortForm.name} onChange={e => setCohortForm(p => ({ ...p, name: e.target.value }))}
          placeholder="e.g. West Africa Fintech Fellowship 2026" />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div className="gm-form-group">
          <label>Starting period</label>
          <input type="text" value={cohortForm.currentPeriod} onChange={e => setCohortForm(p => ({ ...p, currentPeriod: e.target.value }))}
            placeholder="e.g. Q1-2026" />
        </div>
      </div>

      <div className="gm-form-group" style={{ marginTop: 8 }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
          <input type="checkbox" checked={cohortForm.enableMetric}
            onChange={e => setCohortForm(p => ({ ...p, enableMetric: e.target.checked }))} />
          Track a primary impact metric for this cohort
        </label>
        <p style={{ fontSize: '0.75rem', color: '#6b6b6b', margin: '4px 0 0' }}>
          Optional. Examples: "Active learners" for Edtech, "Active users" for Fintech, "Hectares under management" for Agtech.
          Leave unchecked if this programme doesn't track a comparable per-company metric.
        </p>
      </div>

      {cohortForm.enableMetric && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 8 }}>
          <div className="gm-form-group">
            <label>Metric label</label>
            <input type="text" value={cohortForm.metricLabel} onChange={e => setCohortForm(p => ({ ...p, metricLabel: e.target.value }))}
              placeholder="e.g. Active learners" />
          </div>
          <div className="gm-form-group">
            <label>Target per company</label>
            <input type="number" value={cohortForm.metricTarget} onChange={e => setCohortForm(p => ({ ...p, metricTarget: e.target.value }))} />
          </div>
        </div>
      )}

      {/* Rubric assignment */}
      <div className="gm-form-group">
        <label>Assessment rubric</label>
        <select value={cohortForm.rubricTemplateId} onChange={e => setCohortForm(p => ({ ...p, rubricTemplateId: e.target.value }))}>
          {rubrics.map(r => <option key={r.id} value={r.id}>{r.name}{r.context ? ` · ${r.context}` : ''}</option>)}
        </select>
      </div>

      {/* Work plan buckets */}
      <div className="gm-form-group" style={{ marginTop: 8 }}>
        <label>Work plan buckets</label>
        <p style={{ fontSize: '0.75rem', color: '#6b6b6b', marginBottom: 8 }}>
          These are the focus areas for work plans and coaching sessions in this cohort.
        </p>
        {cohortForm.buckets.map((bucket, idx) => (
          <div key={bucket.id} className="gm-bucket-row">
            <span className="gm-bucket-icon">{bucket.icon}</span>
            <input type="text" value={bucket.label}
              onChange={e => {
                const buckets = [...cohortForm.buckets];
                buckets[idx] = { ...buckets[idx], label: e.target.value };
                setCohortForm(p => ({ ...p, buckets }));
              }}
              style={{ flex: 1, border: '1px solid #e2e8f0', borderRadius: 6, padding: '6px 10px', fontSize: '0.82rem', fontFamily: 'inherit' }}
            />
            <button className="gm-btn-icon" style={{ color: '#dc2626' }}
              onClick={() => setCohortForm(p => ({ ...p, buckets: p.buckets.filter((_, i) => i !== idx) }))}>
              ✕
            </button>
          </div>
        ))}

        {/* Add bucket */}
        <div className="gm-bucket-row" style={{ marginTop: 8 }}>
          <input type="text" placeholder="Icon (emoji)" value={newBucket.icon}
            onChange={e => setNewBucket(p => ({ ...p, icon: e.target.value }))}
            style={{ width: 60, border: '1px solid #e2e8f0', borderRadius: 6, padding: '6px 10px', fontSize: '0.82rem', fontFamily: 'inherit' }} />
          <input type="text" placeholder="Bucket label" value={newBucket.label}
            onChange={e => setNewBucket(p => ({ ...p, label: e.target.value }))}
            style={{ flex: 1, border: '1px solid #e2e8f0', borderRadius: 6, padding: '6px 10px', fontSize: '0.82rem', fontFamily: 'inherit' }} />
          <button className="gm-btn gm-btn-sm gm-btn-outline"
            onClick={() => {
              if (!newBucket.label.trim()) return;
              const id = newBucket.label.toLowerCase().replace(/[^a-z0-9]/g, '_');
              setCohortForm(p => ({ ...p, buckets: [...p.buckets, { ...newBucket, id }] }));
              setNewBucket({ id: '', label: '', icon: '📋', description: '' });
            }}>
            + Add
          </button>
        </div>
      </div>
    </FormShell>
  );

  // ── Cohort detail ─────────────────────────────────────────────
  if (view === 'cohortDetail' && selectedCohort) return (
    <CohortDetail
      cohort={selectedCohort}
      program={selectedProgram}
      companies={companies}
      cohorts={cohorts}
      rubrics={rubrics}
      onBack={() => setView('list')}
      onAssignCompany={assignCompanyToCohort}
      onAssignRubric={assignRubricToCohort}
      onReload={load}
      onUpdate={async (updates) => {
        await updateDoc(doc(db, 'cohorts', selectedCohort.id), updates);
        setSelectedCohort(prev => ({ ...prev, ...updates }));
        setCohorts(prev => prev.map(c => c.id === selectedCohort.id ? { ...c, ...updates } : c));
        setSaved(true); setTimeout(() => setSaved(false), 2000);
      }}
    />
  );

  return null;
}

// ── Cohort detail view ────────────────────────────────────────
function CohortDetail({ cohort, program, companies, cohorts, rubrics, onBack, onAssignCompany, onAssignRubric, onUpdate, onReload }) {
  const [saving, setSaving]       = useState(false);
  const [saved, setSaved]         = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [period, setPeriod]       = useState(cohort.currentPeriod || '');
  const [cohortNameDraft, setCohortNameDraft] = useState(cohort.name);
  const [savingName, setSavingName] = useState(false);
  const [rubricHistory, setRubricHistory] = useState([]);
  const [showNewCompany, setShowNewCompany] = useState(false);
  const [companyForm, setCompanyForm] = useState({ name: '', description: '', sector: '', country: '' });
  const [savingCompany, setSavingCompany] = useState(false);

  const cohortCompanies = companies.filter(c => c.cohortId === cohort.id);
  const unassigned = companies.filter(c => !c.cohortId || c.cohortId !== cohort.id);
  const currentRubric = rubrics.find(r => r.id === cohort.rubricTemplateId);

  // Load rubric history
  useEffect(() => {
    async function loadHistory() {
      try {
        const snap = await getDocs(
          collection(db, 'rubricTemplates', cohort.rubricTemplateId || 'tmpl_prog_rfc_ethiopia_2026', 'history')
        );
        setRubricHistory(snap.docs.map(d => ({ id: d.id, ...d.data() }))
          .sort((a,b) => (b.changedAt?.seconds||0) - (a.changedAt?.seconds||0)));
      } catch (err) { /* history may not exist yet */ }
    }
    if (activeTab === 'rubric') loadHistory();
  }, [activeTab, cohort.rubricTemplateId]);

  const savePeriod = async () => {
    setSaving(true);
    await onUpdate({ currentPeriod: period });
    setSaving(false); setSaved(true); setTimeout(() => setSaved(false), 2000);
  };

  const saveCohortName = async () => {
    if (!cohortNameDraft.trim() || cohortNameDraft === cohort.name) return;
    setSavingName(true);
    await onUpdate({ name: cohortNameDraft.trim() });
    setSavingName(false); setSaved(true); setTimeout(() => setSaved(false), 2000);
  };

  const saveNewCompany = async () => {
    if (!companyForm.name.trim()) return;
    setSavingCompany(true);
    try {
      const { addDoc: addDocFn, collection: colFn, serverTimestamp: stsFn } = await import('firebase/firestore');
      const newComp = {
        name: companyForm.name,
        description: companyForm.description || '',
        sector: companyForm.sector || '',
        country: companyForm.country || '',
        cohortId: cohort.id,
        programId: program?.id || '',
        currentStage: '',
        currentStageProgress: 0,
        currentStageUpgrade: '',
        targetStage: '',
        targetStageProgress: 0,
        targetStageUpgrade: '',
        memberUids: [],
        memberEmails: [],
        coachId: '',
        coachName: '',
        createdAt: stsFn(),
      };
      await addDocFn(colFn(db, 'companies'), newComp);
      setCompanyForm({ name: '', description: '', sector: '', country: '' });
      setShowNewCompany(false);
      setSaved(true); setTimeout(() => setSaved(false), 2000);
      if (onReload) await onReload();
    } catch (err) { console.error(err); }
    finally { setSavingCompany(false); }
  };

  const formatDate = (ts) => {
    if (!ts) return '—';
    const d = ts?.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div>
      <button className="gm-back-btn" onClick={onBack}>← All programmes</button>
      <div style={{ marginTop: 16, marginBottom: 24 }}>
        <h2 className="gm-page-title">{cohort.name}</h2>
        <p className="gm-page-sub">{program?.name} · {cohortCompanies.length} companies · Period: {cohort.currentPeriod || '—'}</p>
      </div>

      <div className="gm-config-card" style={{ marginBottom: 16 }}>
        <div className="gm-config-label">Cohort name</div>
        <p className="gm-config-desc">Renaming updates this cohort everywhere it appears — for all companies, coaches, and stakeholders linked to it.</p>
        <div style={{ display: 'flex', gap: 12, marginTop: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <input type="text" value={cohortNameDraft} onChange={e => setCohortNameDraft(e.target.value)}
            style={{ padding: '8px 12px', border: '1.5px solid #e2e8f0', borderRadius: 6, fontSize: '0.875rem', fontFamily: 'inherit', width: 280 }} />
          <button className="gm-btn gm-btn-primary gm-btn-sm" onClick={saveCohortName} disabled={savingName || cohortNameDraft === cohort.name || !cohortNameDraft.trim()}>
            {savingName ? 'Saving…' : 'Rename cohort'}
          </button>
        </div>
      </div>

      {saved && <div className="gm-save-success" style={{ marginBottom: 16 }}>✓ Saved</div>}

      <div className="gm-client-tabs" style={{ marginBottom: 24 }}>
        {['overview', 'companies', 'rubric'].map(tab => (
          <button key={tab} className={`gm-client-tab ${activeTab === tab ? 'gm-client-tab--active' : ''}`}
            onClick={() => setActiveTab(tab)}>
            {tab === 'overview' ? 'Overview & Config' : tab === 'companies' ? 'Companies' : 'Rubric'}
          </button>
        ))}
      </div>

      {/* Overview & Config */}
      {activeTab === 'overview' && (
        <div>
          <div className="gm-config-card" style={{ marginBottom: 16 }}>
            <div className="gm-config-label">Current assessment period</div>
            <div style={{ display: 'flex', gap: 12, marginTop: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <input type="text" value={period} onChange={e => setPeriod(e.target.value)}
                placeholder="e.g. Q2-2026"
                style={{ padding: '8px 12px', border: '1.5px solid #e2e8f0', borderRadius: 6, fontSize: '0.875rem', fontFamily: 'inherit', width: 160 }} />
              <button className="gm-btn gm-btn-primary gm-btn-sm" onClick={savePeriod} disabled={saving}>
                {saving ? 'Saving…' : 'Update period'}
              </button>
            </div>
          </div>

          <div className="gm-config-card">
            <div className="gm-config-label">Work plan buckets</div>
            <div style={{ marginTop: 8 }}>
              {(cohort.buckets || []).map(b => (
                <div key={b.id} className="gm-bucket-row" style={{ marginBottom: 6 }}>
                  <span className="gm-bucket-icon">{b.icon}</span>
                  <span style={{ fontSize: '0.875rem', fontWeight: 600 }}>{b.label}</span>
                  <span style={{ fontSize: '0.75rem', color: '#6b6b6b' }}>{b.description}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Companies */}
      {activeTab === 'companies' && (
        <div>
          {/* New company form */}
          <div style={{ marginBottom: 16 }}>
            {!showNewCompany ? (
              <button className="gm-btn gm-btn-primary gm-btn-sm" onClick={() => setShowNewCompany(true)}>
                + Add new company
              </button>
            ) : (
              <div className="gm-config-card" style={{ marginBottom: 16 }}>
                <div className="gm-config-label">New company</div>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginTop:12 }}>
                  <div className="gm-form-group" style={{ gridColumn:'1 / span 2' }}>
                    <label>Company name *</label>
                    <input type="text" value={companyForm.name} onChange={e => setCompanyForm(p => ({ ...p, name: e.target.value }))} placeholder="e.g. PayFlow Ghana" />
                  </div>
                  <div className="gm-form-group" style={{ gridColumn:'1 / span 2' }}>
                    <label>Description</label>
                    <input type="text" value={companyForm.description} onChange={e => setCompanyForm(p => ({ ...p, description: e.target.value }))} placeholder="One line description" />
                  </div>
                  <div className="gm-form-group">
                    <label>Sector</label>
                    <input type="text" list="sector-list" value={companyForm.sector} onChange={e => setCompanyForm(p => ({ ...p, sector: e.target.value }))} placeholder="e.g. Edtech, Fintech" />
                    <datalist id="sector-list">
                      {[...new Set(companies.map(c => c.sector).filter(Boolean))].map(s => <option key={s} value={s} />)}
                    </datalist>
                  </div>
                  <div className="gm-form-group">
                    <label>Country</label>
                    <input type="text" list="country-list" value={companyForm.country} onChange={e => setCompanyForm(p => ({ ...p, country: e.target.value }))} placeholder="e.g. Ghana" />
                    <datalist id="country-list">
                      {[...new Set(companies.map(c => c.country).filter(Boolean))].map(c => <option key={c} value={c} />)}
                    </datalist>
                  </div>
                  <p style={{ fontSize:'0.75rem', color:'#94a3b8', gridColumn:'1 / span 2', fontStyle:'italic', margin:0 }}>
                    Baseline and target stages are set during immersion week assessments — no need to select them now.
                  </p>
                </div>
                <div style={{ display:'flex', gap:8, marginTop:12 }}>
                  <button className="gm-btn gm-btn-primary gm-btn-sm" onClick={saveNewCompany} disabled={savingCompany || !companyForm.name.trim()}>
                    {savingCompany ? 'Saving…' : 'Create company'}
                  </button>
                  <button className="gm-btn gm-btn-outline gm-btn-sm" onClick={() => setShowNewCompany(false)}>Cancel</button>
                </div>
              </div>
            )}
          </div>

          <h3 className="gm-section-title">Companies in this cohort ({cohortCompanies.length})</h3>
          {cohortCompanies.length === 0 && (
            <div className="gm-empty-state"><p>No companies assigned yet.</p></div>
          )}
          <div className="gm-table-wrap">
            {cohortCompanies.length > 0 && (
              <table className="gm-table">
                <thead><tr><th>Company</th><th>Stage</th><th>Coach</th><th></th></tr></thead>
                <tbody>
                  {cohortCompanies.map(c => (
                    <tr key={c.id}>
                      <td style={{ fontWeight: 600 }}>{c.name}</td>
                      <td><span style={{ color: STAGE_COLORS[c.currentStage], fontSize: '0.78rem', fontWeight: 600 }}>{c.currentStage}</span></td>
                      <td style={{ fontSize: '0.78rem' }}>{c.coachName || '—'}</td>
                      <td>
                        <button className="gm-btn gm-btn-sm gm-btn-outline"
                          style={{ color: '#dc2626', borderColor: '#fecaca', fontSize: '0.72rem' }}
                          onClick={() => onAssignCompany(c.id, null)}>
                          Remove
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {unassigned.length > 0 && (
            <div style={{ marginTop: 24 }}>
              <h3 className="gm-section-title">Add companies to this cohort</h3>
              <div className="gm-table-wrap">
                <table className="gm-table">
                  <thead><tr><th>Company</th><th>Currently in</th><th></th></tr></thead>
                  <tbody>
                    {unassigned.map(c => {
                      const currentCohort = cohorts?.find(co => co.id === c.cohortId);
                      return (
                        <tr key={c.id}>
                          <td style={{ fontWeight: 600 }}>{c.name}</td>
                          <td style={{ fontSize: '0.78rem', color: '#6b6b6b' }}>{currentCohort?.name || 'Unassigned'}</td>
                          <td>
                            <button className="gm-btn gm-btn-sm gm-btn-primary"
                              onClick={() => onAssignCompany(c.id, cohort.id)}>
                              + Add
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Rubric */}
      {activeTab === 'rubric' && (
        <div>
          <div className="gm-config-card" style={{ marginBottom: 16 }}>
            <div className="gm-config-label">Assigned rubric</div>
            <div style={{ display: 'flex', gap: 12, marginTop: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <select
                defaultValue={cohort.rubricTemplateId}
                onChange={e => onAssignRubric(cohort.id, e.target.value)}
                style={{ padding: '8px 12px', border: '1.5px solid #0369a1', borderRadius: 6, fontSize: '0.875rem', fontFamily: 'inherit', color: '#0369a1', fontWeight: 600, minWidth: 280 }}>
                {rubrics.map(r => (
                  <option key={r.id} value={r.id}>{r.name}{r.context ? ` · ${r.context}` : ''}</option>
                ))}
              </select>
              {currentRubric && (
                <span className="gm-validated-badge">Currently: {currentRubric.name}</span>
              )}
            </div>
          </div>

          {/* Rubric history */}
          <div className="gm-section">
            <h3 className="gm-section-title">Rubric change history</h3>
            {rubricHistory.length === 0 && (
              <div className="gm-empty-state"><p>No rubric changes recorded yet. Edits made in the rubric editor will appear here.</p></div>
            )}
            {rubricHistory.map(h => (
              <div key={h.id} className="gm-session-card" style={{ marginBottom: 8 }}>
                <div className="gm-session-card-header" style={{ cursor: 'default' }}>
                  <div className="gm-session-card-left">
                    <div className="gm-session-date">{formatDate(h.changedAt)}</div>
                    <div className="gm-session-meta">
                      <span>by {h.changedBy}</span>
                      <span>{h.questionId} — {h.changeType}</span>
                    </div>
                    {h.previousText && (
                      <p style={{ fontSize: '0.75rem', color: '#94a3b8', margin: '4px 0 0', textDecoration: 'line-through' }}>
                        Before: {h.previousText.slice(0, 100)}
                      </p>
                    )}
                    {h.newText && (
                      <p style={{ fontSize: '0.75rem', color: '#1e293b', margin: '2px 0 0' }}>
                        After: {h.newText.slice(0, 100)}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Reusable form shell ───────────────────────────────────────
function FormShell({ title, onBack, onSave, saving, error, children }) {
  return (
    <div>
      <button className="gm-back-btn" onClick={onBack}>← Back</button>
      <div style={{ marginTop: 16, marginBottom: 24 }}>
        <h2 className="gm-page-title">{title}</h2>
      </div>
      {error && <div className="gm-alert gm-alert-error" style={{ marginBottom: 12 }}>{error}</div>}
      <div className="gm-config-card">
        {children}
        <div style={{ display: 'flex', gap: 12, marginTop: 20 }}>
          <button className="gm-btn gm-btn-primary" onClick={onSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </button>
          <button className="gm-btn gm-btn-outline" onClick={onBack}>Cancel</button>
        </div>
      </div>
    </div>
  );
}
